import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Collapsible } from '@/App/tiles/shared/Collapsible.tsx';
import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { getTargetAccumulatorMarginData } from '@/store/targetAccumulator/selectors.ts';

const CommercialMargin = () => {
  const { formatMessage, locale } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();
  const { markupCurrency, currencyPair, marginData } = useTargetAccumulatorSelector((targetAccumulator) => ({
    markupCurrency: targetAccumulator.properties.markupCurrency,
    currencyPair: targetAccumulator.properties.currencyPair,
    marginData: getTargetAccumulatorMarginData(targetAccumulator),
  }));

  const [ccy1, ccy2] = currencyPair?.split('/') ?? ['', ''];

  const handleNotionalCurrencyClick = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Notional currency clicked',
      isNotDefined(currencyPair) ? 'cleared' : currencyPair,
    );

    if (isNotDefined(currencyPair)) {
      return null;
    }

    return checkTargetAccumulatorWithTradeCapture({
      markupCurrency: markupCurrency === ccy2 ? ccy1 : ccy2,
    });
  }, [sgwtWebAnalytics, currencyPair, checkTargetAccumulatorWithTradeCapture, markupCurrency, ccy1, ccy2]);

  const markup = markupCurrency === ccy1 ? marginData?.markupCcy1 : marginData?.markupCcy2;

  return (
    <Collapsible
      id="commercialMargin"
      label={formatMessage({ id: 'accumulator.collapse.commercialMargin.label' })}
      dataE2e="margin"
    >
      <TargetAccumulatorFormField formattedMessageId="targetAccumulator.form.marginAmount">
        <div className="d-flex">
          <SgNumberInput locale={locale} value={markup} data-e2e="marginAmountInput" disabled />
          <button
            tabIndex={-1}
            type="button"
            data-e2e="marginAmount-btn"
            onClick={handleNotionalCurrencyClick}
            className="px-1 btn btn-discreet-primary btn-sm"
          >
            {markupCurrency}
          </button>
        </div>
      </TargetAccumulatorFormField>
    </Collapsible>
  );
};

export default CommercialMargin;
